import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {AxiosError} from "axios";

import {Button} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {useToast} from "@/src/modules/core/hooks";

import {PasswordInput} from "./password-input";
import {useChangePassword} from "../hooks";
import {createChangePasswordSchema, type ChangePasswordFormValues} from "../schemas";
import {useSessionStore} from "../store/session";

export function ChangePasswordModal() {
  const {t} = useTranslation("auth");
  const {t: tCommon} = useTranslation("common");
  const toast = useToast();
  const session = useSessionStore((state) => state.session);
  const setSession = useSessionStore((state) => state.setSession);
  const [apiPasswordError, setApiPasswordError] = useState<string | null>(null);
  const changePasswordSchema = useMemo(() => createChangePasswordSchema(t), [t]);

  const form = useForm<ChangePasswordFormValues>({
    defaultValues: {
      password: "",
      password_confirm: "",
    },
    resolver: zodResolver(changePasswordSchema),
  });

  const {mutateAsync: changePassword, isPending} = useChangePassword();

  const handleSubmit = async (data: ChangePasswordFormValues) => {
    setApiPasswordError(null);

    try {
      await changePassword({password: data.password});

      if (session) {
        setSession({
          ...session,
          requires_password_change: false,
        });
      }

      toast.success(t("change_password.success"));
    } catch (error) {
      let errorMessage = t("change_password.failed");
      let passwordError: string | null = null;

      if (error instanceof AxiosError) {
        const responseData = error.response?.data as {
          message?: string;
          errors?: {password?: string[]};
        };

        if (responseData?.message) {
          errorMessage = responseData.message;
        }

        if (
          responseData?.errors?.password &&
          Array.isArray(responseData.errors.password) &&
          responseData.errors.password.length > 0
        ) {
          passwordError = responseData.errors.password[0];
          setApiPasswordError(passwordError);
        }
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    }
  };

  const isOpen = session?.requires_password_change === true;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl">{t("change_password.title")}</DialogTitle>
          <DialogDescription className="text-center">
            {t("change_password.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="password"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>{t("reset.password")}</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password ? (
                    <FormError error={formState.errors.password.message} />
                  ) : null}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password_confirm"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>{t("reset.confirm_password")}</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password_confirm ? (
                    <FormError error={formState.errors.password_confirm.message} />
                  ) : null}
                  {apiPasswordError ? (
                    <p className="mt-1 text-sm text-red-600">{apiPasswordError}</p>
                  ) : null}
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  {tCommon("processing")}
                  <Loader2 className="mr-2 size-4 animate-spin" />
                </>
              ) : (
                t("change_password.title")
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
